import React, { useState, useCallback } from 'react';
import { Paper, Typography, IconButton, Tooltip, Stack, useMediaQuery, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArticleIcon from '@mui/icons-material/Article';
import ArchiveIcon from '@mui/icons-material/Archive';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { toast } from 'react-toastify';
import { Loader } from '../../../../../components/loader';
import { fileUpload, getFileDownloadUrl } from '../../../../../services/fileUploadService';

// ---------------------------
// File-type icon resolver
// ---------------------------
const getFileIcon = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();

  if (['png', 'jpg', 'jpeg'].includes(ext)) return <ImageIcon fontSize="large" color="primary" />;
  if (['mp4', 'mov', 'avi'].includes(ext)) return <VideoFileIcon fontSize="large" color="secondary" />;
  if (ext === 'pdf') return <PictureAsPdfIcon fontSize="large" color="error" />;
  if (['csv', 'xls', 'xlsx'].includes(ext)) return <TableChartIcon fontSize="large" color="success" />;
  if (['doc', 'docx'].includes(ext)) return <ArticleIcon fontSize="large" color="info" />;
  if (['zip', 'rar'].includes(ext)) return <ArchiveIcon fontSize="large" />;

  return <DescriptionIcon fontSize="large" />;
};

// ---------------------------
// Single File Card
// ---------------------------
const AttachmentCard = ({ file, onDelete, onClick, width }) => (
  <Paper
    onClick={onClick}
    elevation={0}
    sx={{
      border: '1px solid #ddd',
      borderRadius: '10px',
      padding: '6px 10px',
      display: 'flex',
      alignItems: 'center',
      width,
      gap: 1,
      position: 'relative',
      cursor: 'pointer',
      height: 60, // FIXED HEIGHT
      minHeight: 60,
    }}
  >
    {getFileIcon(file.name)}

    <Tooltip title={file.name} placement="top">
      <Typography
        variant="caption"
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flexGrow: 1,
        }}
      >
        {file.name}
      </Typography>
    </Tooltip>

    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        onDelete();
      }}
      sx={{ position: 'absolute', top: 2, right: 2, height: 18, width: 18 }}
    >
      <CloseIcon sx={{ fontSize: '12px' }} />
    </IconButton>
  </Paper>
);

// ---------------------------
// Main Uploader
// ---------------------------
const AttachmentUploader = ({ attachments, setAttachments }) => {
  const theme = useTheme();
  const [isUploading, setIsUploading] = useState(false);

  const cardWidth = 80;

  const handleFileClick = useCallback(async (file) => {
    try {
      const isS3Key =
        /^(company_|user_|system_|common_)/.test(file.key) || /^\d+_/.test(file.key) || !file.key.includes('://');

      if (isS3Key) {
        const downloadUrl = await getFileDownloadUrl(file.key);
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      const isAbsolute = /^https?:\/\//i.test(file.key);
      const fileUrl = isAbsolute ? file.key : `https://${file.key}`;
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error('Failed to open file');
      console.error('Error opening file:', error);
    }
  }, []);

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    // Reset input so the same file can be re-selected after removal
    event.target.value = '';

    if (files.length === 0) return;

    const validFiles = files.filter((file) => {
      if (file.size > 100 * 1024 * 1024) {
        toast.error(`"${file.name}" exceeds the 100MB limit and was skipped`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setIsUploading(true);
    try {
      const uploaded = await Promise.all(
        validFiles.map(async (file) => {
          const res = await fileUpload('task_attachments', file.name, file);
          return { key: res.key, name: file.name };
        })
      );
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch {
      toast.error('One or more file uploads failed');
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  return (
    <div style={{ marginTop: 10 }}>
      <Typography sx={{ mb: 1 }} variant="subtitle2">
        Attachments
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ rowGap: 2 }}>
        {attachments.map((file, index) => (
          <AttachmentCard
            key={file.key}
            file={file}
            width={cardWidth}
            onClick={() => handleFileClick(file)}
            onDelete={() => removeFile(index)}
          />
        ))}

        {/* Upload box */}
        <label
          htmlFor="task-attachment-input"
          style={{
            width: cardWidth,
            height: 60,
            border: '1px dashed #999',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {isUploading ? <Loader size={20} /> : <AttachFileIcon />}
          <input
            type="file"
            id="task-attachment-input"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </label>
      </Stack>
    </div>
  );
};

export default AttachmentUploader;
